import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './OrderDetail.css';

const OrderDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const isNewOrder = searchParams.get('success') === 'true';

  useEffect(() => {
    fetchOrder();
    // Scroll to top when page loads (especially for success page)
    window.scrollTo(0, 0);
  }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await fetch(`/api/orders/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setOrder(data);
      }
    } catch (error) {
      console.error('Failed to fetch order:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: 'warning',
      processing: 'primary',
      shipped: 'primary',
      delivered: 'success',
      cancelled: 'danger'
    };
    return statusMap[status] || 'secondary';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return `${price.toFixed(2)} ${t('currency')}`;
  };

  const getStatusText = (status) => {
    return t(status) || status.charAt(0).toUpperCase() + status.slice(1);
  };

  if (loading) {
    return (
      <div className="order-page">
        <div className="container">
          <div className="loading-box">
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-page">
        <div className="container">
          <div className="empty-state">
            <span className="empty-icon">📦</span>
            <h2>{t('noOrders')}</h2>
            <p>{t('noOrdersDesc')}</p>
            <Link to="/orders" className="btn btn-primary">{t('backToOrders')}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
      <div className={`order-page ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container">

          {/* Success Message */}
        {isNewOrder && (
          <div className="success-message">
            <span className="check-icon">✓</span>
            <div>
              <strong>{t('orderPlaced')}</strong>
              <p>{t('orderPlacedDesc')}</p>
            </div>
          </div>
        )}

        {/* Back Link */}
        <Link to="/orders" className="back-btn">
          {isRTL ? '→' : '←'} {t('backToOrders')}
        </Link>

        {/* Main Card */}
        <div className="order-card">

          {/* Header */}
          <div className="order-header">
            <div className="order-title">
              <h1>{isRTL ? 'طلب' : 'Order'} <span>{order.id.slice(0, 8)}</span></h1>
              <p>{formatDate(order.createdAt)}</p>
            </div>
            <div className={`status-pill ${getStatusBadge(order.status)}`}>
              {getStatusText(order.status)}
            </div>
          </div>

          {/* Order Items */}
          <div className="section">
            <h3>{t('orderItems')}</h3>
            <div className="items-container">
              {order.items.map((item, index) => (
                <div key={index} className="product-item">
                  <img src={item.image} alt={isRTL && item.nameAr ? item.nameAr : item.name} />
                  <div className="product-details">
                    <span className="product-name">{isRTL && item.nameAr ? item.nameAr : item.name}</span>
                    <span className="product-qty">{t('quantity')}: {item.quantity}</span>
                  </div>
                  <span className="product-price">{formatPrice(item.price * item.quantity)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="two-columns">

            {/* Shipping Info */}
            <div className="section">
              <h3>{t('shippingAddress')}</h3>
              <div className="info-box">
                <p className="name">{order.shippingAddress.fullName}</p>
                <p>{order.shippingAddress.phone}</p>
                <p>{order.shippingAddress.address}</p>
                <p>{order.shippingAddress.city}, {order.shippingAddress.emirate}</p>
                <p>{order.shippingAddress.country}</p>
              </div>
            </div>

            {/* Order Summary */}
            <div className="section">
              <h3>{t('orderSummary')}</h3>
              <div className="summary-box">
                <div className="summary-line">
                  <span>{t('subtotal')}</span>
                  <span>{formatPrice(order.subtotal)}</span>
                </div>
                {order.discount > 0 && (
                  <div className="summary-line" style={{ color: '#059669' }}>
                    <span>{t('discount')} {order.promoCode && <span style={{ fontSize: '12px', background: '#d1fae5', padding: '2px 6px', borderRadius: '4px' }}>{order.promoCode}</span>}</span>
                    <span>-{formatPrice(order.discount)}</span>
                  </div>
                )}
                <div className="summary-line">
                  <span>{t('shipping')}</span>
                  <span>{formatPrice(order.shipping)}</span>
                </div>
                <div className="summary-line total">
                  <span>{t('total')}</span>
                  <span>{formatPrice(order.total)}</span>
                </div>
              </div>
            </div>
          </div>

          </div>
        </div>
      </div>
  );
};

export default OrderDetail;
